import { Component, OnInit, NO_ERRORS_SCHEMA, HostListener } from '@angular/core';
import { DashboardService } from './dashboard.service';
import { RadialGaugeChartComponent } from '../../shared/components/radial-gauge-chart/radial-gauge-chart.component';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { GaugeChartData } from '../../shared/models/gauge-chart.model';
import { LoaderComponent } from '../../shared/components/loader/loader.component';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        RadialGaugeChartComponent,
        LoaderComponent
    ],
    schemas: [NO_ERRORS_SCHEMA]
})
export class DashboardComponent implements OnInit {
    // Add loading state
    // isLoading: boolean = true;
    activeTooltip: string | null = null;
    //new loading states
    loadingStates: { [key: string]: boolean } = {
        publishedArticles: false,
        daysToPublish: false,
        authorPool: false,
        revenue: false
    };
    
    // Published Articles gauges
    publishedArticlesGauges: GaugeChartData[] = [
        { title: 'AIPP Articles', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 20000, color: '#4169E1', targetColor: '#3498db', label: 'Published Articles' },
        { title: 'Hybrid OA Articles', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 20000, color: '#4169E1', targetColor: '#3498db', label: 'Published Articles' },
        { title: 'Gold OA Articles', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 5000, color: '#4169E1', targetColor: '#3498db', label: 'Published Articles' }
    ];

    // Author Pool gauges
    authorPoolGauges: GaugeChartData[] = [
        { title: 'Submitting Authors', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 200000, color: '#4169E1', targetColor: '#3498db', label: 'Authors' },
        { title: 'Marketing Opt-In', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 400000, color: '#4169E1', targetColor: '#3498db', label: 'Authors' }
    ];

    // Days to Publish gauge
    daysToPublishGauge: GaugeChartData[] = [
        { title: 'Avg. Days to Publish - AIPP only', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 120, color: '#4169E1', targetColor: '#3498db', label: 'Days' }
    ];

    // Revenue gauges
    revenueGauges: GaugeChartData[] = [
        { title: 'APC Revenue - AIPP only', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 20, color: '#4169E1', targetColor: '#3498db', label: 'Million $' },
        { title: 'Total Revenue', value: 0, currentYearTarget: 0, currentMonthTarget: 0, currentMonthActual: 0, percentChangeTargetYearOverYear: 0, percentChangeActualVsTarget: 0, maxValue: 80, color: '#4169E1', targetColor: '#3498db', label: 'Million $' }
    ];

    revenueAsOfLabel: string = '';

    // Tooltip system for dashboard sections
    // Each section has an info button (i) that displays relevant definitions
    // Tooltips are responsive and adapt to mobile/desktop views
    tooltipContent= {
        publishedArticles: [
            {key: 'AIPP Articles', definition: 'Total YTD published articles from AIPP journals.'},
            {key: 'Hybrid OA Articles', definition: 'Total YTD hybrid OA articles from AIPP journals.'},
            {key: 'Gold OA Articles', definition: 'Total YTD gold OA articles from AIPP journals.'}
        ],
        authorPool: [
            {key: 'Submitting Authors', definition: 'Total distinct count of AIPP authors who\'ve submitted from 2022 to present.'},
            {key: 'Marketing Opt-In', definition: 'Total distinct count of AIPP and Partner authors who\'ve opted in to receive marketing alerts.'}
        ],
        daysToPublish: [
            {key: 'Avg. Days to Publish - AIPP Journals Only', definition: 'The average time taken (in days) from when an author submits an article to when it is officially published in 2025.'}
        ],
        revenue: [
            {key: 'APC Revenue - AIPP Jounals Only', definition: 'Revenue earned specifically from Article Processing Charges (fees paid by authors or funders to make their article Open Access) for Year Till Date.'},
            {key: 'Total Revenue', definition: 'Overall earnings from all publishing activities (subscriptions, Open Access, services, etc.) for Year Till Date.'}
        ]

    }

    // Add a property to track screen size
    isMobileView: boolean = false;

    constructor(private dashboardService: DashboardService) {
        // Check initial screen size
        this.checkScreenSize();
    }

    ngOnInit() {
        const today = new Date();
        const monthOffset = today.getDate() >= 20 ? 1 : 2;
        const asOf = new Date(today.getFullYear(), today.getMonth() - monthOffset, 1);
        this.revenueAsOfLabel = asOf.toLocaleString('default', { month: 'long', year: 'numeric' });

        this.loadData();
    }
    
    // Listen for window resize events
    @HostListener('window:resize', ['$event'])
    onResize() {
        this.checkScreenSize();
    }
    
    // Listen for document clicks to close tooltips when clicking outside
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target.closest('.chart-info-tooltip') && !target.closest('.chart-info-btn')) {
            this.activeTooltip = null;
        }
    }
    
    // Listen for window scroll to close tooltips for better UX
    @HostListener('window:scroll', [])
    onWindowScroll() {
        if (this.activeTooltip) {
            this.activeTooltip = null;
        }
    }
    
    // Listen for escape key to close tooltips for keyboard accessibility
    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        if (this.activeTooltip) {
            this.activeTooltip = null;
        }
    }
    
    // Check if we're on a mobile screen for responsive tooltip behavior
    private checkScreenSize() {
        this.isMobileView = window.innerWidth < 768;
    }

    // Toggle tooltip visibility for a specific section
    // If the same tooltip is clicked again, it closes
    // If a different tooltip is clicked, it switches to that one
    toggleTooltip(tooltipType: string) : void {
        this.activeTooltip = this.activeTooltip === tooltipType ? null : tooltipType;
    }

    // Helper methods for loading states
    hasAnyLoadingState(): boolean {
        return Object.values(this.loadingStates).some(state => state);
    }

    setLoadingState(key: string, loading: boolean) {
        this.loadingStates[key] = loading;
    }

    resetAllLoadingStates() {
        Object.keys(this.loadingStates).forEach(key => {
            this.loadingStates[key] = false;
        });
    }

    private loadData(): void {
        // Set loading to true when starting to fetch data
        // this.isLoading = true;
        
        // Set loading states for each section
        this.setLoadingState('publishedArticles', true);
        this.setLoadingState('daysToPublish', true);
        this.setLoadingState('authorPool', true);
        this.setLoadingState('revenue', true);
        
        forkJoin({
            publishedArticles: this.dashboardService.getPublishedArticlesData(),
            authorPool: this.dashboardService.getAuthorPoolData(),
            daysToPublish: this.dashboardService.getDaysToPublishData(),
            revenue: this.dashboardService.getRevenueData()
        }).subscribe({
            next: (data) => {
                // Process Published Articles data
                const publishedArticlesData = data.publishedArticles;
                if (publishedArticlesData.length >= 3) {
                    // AIPP
                    this.publishedArticlesGauges[0].value = publishedArticlesData[0].currentMonthActual || 0;
                    this.publishedArticlesGauges[0].currentYearTarget = publishedArticlesData[0].currentYearTarget || 0;
                    this.publishedArticlesGauges[0].currentMonthTarget = publishedArticlesData[0].currentMonthTarget || 0;
                    this.publishedArticlesGauges[0].currentMonthActual = publishedArticlesData[0].currentMonthActual || 0;
                    this.publishedArticlesGauges[0].percentChangeTargetYearOverYear = publishedArticlesData[0].percentChangeTargetYearOverYear || 0;
                    this.publishedArticlesGauges[0].percentChangeActualVsTarget = publishedArticlesData[0].percentChangeActualVsTarget || 0;
                    this.publishedArticlesGauges[0].maxValue = Math.max(
                        publishedArticlesData[0].currentYearTarget || 0, 
                        publishedArticlesData[0].currentMonthActual || 0
                    ) * 1.1;
                    
                    // Hybrid
                    this.publishedArticlesGauges[1].value = publishedArticlesData[1].currentMonthActual || 0;
                    this.publishedArticlesGauges[1].currentYearTarget = publishedArticlesData[1].currentYearTarget || 0;
                    this.publishedArticlesGauges[1].currentMonthTarget = publishedArticlesData[1].currentMonthTarget || 0;
                    this.publishedArticlesGauges[1].currentMonthActual = publishedArticlesData[1].currentMonthActual || 0;
                    this.publishedArticlesGauges[1].percentChangeTargetYearOverYear = publishedArticlesData[1].percentChangeTargetYearOverYear || 0;
                    this.publishedArticlesGauges[1].percentChangeActualVsTarget = publishedArticlesData[1].percentChangeActualVsTarget || 0;
                    this.publishedArticlesGauges[1].maxValue = Math.max(
                        publishedArticlesData[1].currentYearTarget || 0, 
                        publishedArticlesData[1].currentMonthActual || 0
                    ) * 1.1;
                    
                    // Gold OA
                    this.publishedArticlesGauges[2].value = publishedArticlesData[2].currentMonthActual || 0;
                    this.publishedArticlesGauges[2].currentYearTarget = publishedArticlesData[2].currentYearTarget || 0;
                    this.publishedArticlesGauges[2].currentMonthTarget = publishedArticlesData[2].currentMonthTarget || 0;
                    this.publishedArticlesGauges[2].currentMonthActual = publishedArticlesData[2].currentMonthActual || 0;
                    this.publishedArticlesGauges[2].percentChangeTargetYearOverYear = publishedArticlesData[2].percentChangeTargetYearOverYear || 0;
                    this.publishedArticlesGauges[2].percentChangeActualVsTarget = publishedArticlesData[2].percentChangeActualVsTarget || 0;
                    this.publishedArticlesGauges[2].maxValue = Math.max(
                        publishedArticlesData[2].currentYearTarget || 0, 
                        publishedArticlesData[2].currentMonthActual || 0
                    ) * 1.1;
                }
                this.setLoadingState('publishedArticles', false);

                // Process Author Pool data
                const authorPoolData = data.authorPool;
                if (authorPoolData.length >= 2) {
                    // Sub-Authors
                    this.authorPoolGauges[0].value = authorPoolData[0].currentMonthActual || 0;
                    this.authorPoolGauges[0].currentYearTarget = authorPoolData[0].currentYearTarget || 0;
                    this.authorPoolGauges[0].currentMonthTarget = authorPoolData[0].currentMonthTarget || 0;
                    this.authorPoolGauges[0].currentMonthActual = authorPoolData[0].currentMonthActual || 0;
                    this.authorPoolGauges[0].percentChangeTargetYearOverYear = authorPoolData[0].percentChangeTargetYearOverYear || 0;
                    this.authorPoolGauges[0].percentChangeActualVsTarget = authorPoolData[0].percentChangeActualVsTarget || 0;
                    this.authorPoolGauges[0].maxValue = Math.max(
                        authorPoolData[0].currentYearTarget || 0, 
                        authorPoolData[0].currentMonthActual || 0
                    ) * 1.1;
                    
                    // Marketing Opt-In
                    this.authorPoolGauges[1].value = authorPoolData[1].currentMonthActual || 0;
                    this.authorPoolGauges[1].currentYearTarget = authorPoolData[1].currentYearTarget || 0;
                    this.authorPoolGauges[1].currentMonthTarget = authorPoolData[1].currentMonthTarget || 0;
                    this.authorPoolGauges[1].currentMonthActual = authorPoolData[1].currentMonthActual || 0;
                    this.authorPoolGauges[1].percentChangeTargetYearOverYear = authorPoolData[1].percentChangeTargetYearOverYear || 0;
                    this.authorPoolGauges[1].percentChangeActualVsTarget = authorPoolData[1].percentChangeActualVsTarget || 0;
                    this.authorPoolGauges[1].maxValue = Math.max(
                        authorPoolData[1].currentYearTarget || 0, 
                        authorPoolData[1].currentMonthActual || 0
                    ) * 1.1;
                }
                this.setLoadingState('authorPool', false);

                // Process Days to Publish data
                const daysToPublishData = data.daysToPublish;
                if (daysToPublishData.length > 0) {
                    this.daysToPublishGauge[0].value = daysToPublishData[0].currentMonthActual || 0;
                    this.daysToPublishGauge[0].currentYearTarget = daysToPublishData[0].currentYearTarget || 0;
                    this.daysToPublishGauge[0].currentMonthTarget = daysToPublishData[0].currentMonthTarget || 0;
                    this.daysToPublishGauge[0].currentMonthActual = daysToPublishData[0].currentMonthActual || 0;
                    this.daysToPublishGauge[0].percentChangeTargetYearOverYear = daysToPublishData[0].percentChangeTargetYearOverYear || 0;
                    this.daysToPublishGauge[0].percentChangeActualVsTarget = daysToPublishData[0].percentChangeActualVsTarget || 0;
                    this.daysToPublishGauge[0].maxValue = Math.max(
                        daysToPublishData[0].currentYearTarget || 0, 
                        daysToPublishData[0].currentMonthActual || 0
                    ) * 1.2;
                }
                this.setLoadingState('daysToPublish', false);

                // Process Revenue data
                const revenueData = data.revenue;
                if (revenueData.length >= 2) {
                    // APC Revenue
                    this.revenueGauges[0].value = revenueData[0].currentMonthActual || 0;
                    this.revenueGauges[0].currentYearTarget = revenueData[0].currentYearTarget || 0;
                    this.revenueGauges[0].currentMonthTarget = revenueData[0].currentMonthTarget || 0;
                    this.revenueGauges[0].currentMonthActual = revenueData[0].currentMonthActual || 0;
                    this.revenueGauges[0].percentChangeTargetYearOverYear = revenueData[0].percentChangeTargetYearOverYear || 0;
                    this.revenueGauges[0].percentChangeActualVsTarget = revenueData[0].percentChangeActualVsTarget || 0;
                    this.revenueGauges[0].maxValue = Math.max(
                        revenueData[0].currentYearTarget || 0, 
                        revenueData[0].currentMonthActual || 0
                    ) * 1.2;
                    
                    // Total Revenue
                    this.revenueGauges[1].value = revenueData[1].currentMonthActual || 0;
                    this.revenueGauges[1].currentYearTarget = revenueData[1].currentYearTarget || 0;
                    this.revenueGauges[1].currentMonthTarget = revenueData[1].currentMonthTarget || 0;
                    this.revenueGauges[1].currentMonthActual = revenueData[1].currentMonthActual || 0;
                    this.revenueGauges[1].percentChangeTargetYearOverYear = revenueData[1].percentChangeTargetYearOverYear || 0;
                    this.revenueGauges[1].percentChangeActualVsTarget = revenueData[1].percentChangeActualVsTarget || 0;
                    this.revenueGauges[1].maxValue = Math.max(
                        revenueData[1].currentYearTarget || 0, 
                        revenueData[1].currentMonthActual || 0
                    ) * 1.2;
                }
                this.setLoadingState('revenue', false);
                
                // this.isLoading = false; 
            },
            error: (error) => {
                console.error('Error loading dashboard data:', error);
                // Also set loading to false on error
                // this.isLoading = false;
                
                // Reset all loading states on error
                this.resetAllLoadingStates();
            }
        });
    }
} 